import { ColumnsType } from 'antd/lib/table';
import { getTableScrollX } from '@/utils/table';
import moment from 'moment';
import StatusTag from '@/components/_common/StatusTag';
import {
  ManifestationRecipientInvoice,
  RecipientInvoiceFormValues,
  RecipientInvoiceSituation,
  RecipientInvoiceSituationLabel,
} from '@/core/domain/invoice/recipientInvoice/recipientInvoice.types';
import { useRecipientInvoiceList } from '@/core/domain/invoice/recipientInvoice/recipientInvoice.hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import { EditIcon, InfiniteTable } from '@/components/_common';
import { FormattedMessage } from 'react-intl';
import RecipientInvoiceEditModal from '../RecipientInvoiceEditModal';
import { useDebounce, useDisclosure, useReportGenerate } from '@/hooks';
import { useCallback, useState } from 'react';
import { ManifestNotesFormValues } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.types';
import DownloadIcon from '@/components/_common/DownloadIcon';
import { Space, Tooltip } from 'antd';
import { useArchiveGetArchive } from '@/core/domain/archive/Archive/Archive.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

interface Props {
  manifestationItems: ManifestationRecipientInvoice[];
  setManifestationItems: React.Dispatch<
    React.SetStateAction<ManifestationRecipientInvoice[]>
  >;
  formValues: RecipientInvoiceFormValues;
}

const ManifestationRecipientList = ({
  formValues,
  manifestationItems,
  setManifestationItems,
}: Props) => {
  const formValuesDebounce = useDebounce(formValues);
  const { data, isLoading, fetchNextPage, hasNextPage } =
    useRecipientInvoiceList(formValuesDebounce);
  const { mutateAsync: downloadXmlMutate } = useArchiveGetArchive();
  const report = useReportGenerate();

  const [manifestationItemSelected, setManifestationItemSelect] =
    useState<ManifestationRecipientInvoice>();
  const [modalEditOpened, modalEditAction] = useDisclosure(false, {
    onClose: () => setManifestationItemSelect(undefined),
  });

  const recipientInvoiceList = flatInfiniteData(data);

  function handleEditInvoice(row: ManifestationRecipientInvoice) {
    if (row.id) {
      setManifestationItemSelect(row);

      modalEditAction.open();
    }
  }

  const handleManifestationItems = useCallback(
    (values: ManifestNotesFormValues) => {
      if (manifestationItemSelected) {
        setManifestationItems((prev) => {
          const hasItem = prev.some(
            (item) => item.id === manifestationItemSelected.id,
          );

          if (hasItem) {
            return prev.map((item) => {
              if (item.id === manifestationItemSelected.id) {
                return {
                  ...item,
                  manifestType: values.type,
                  reason: values.reason,
                };
              }
              return item;
            });
          }

          return [
            ...prev,
            {
              ...manifestationItemSelected,
              manifestType: values.type,
              reason: values.reason,
            },
          ];
        });
      }
    },

    [manifestationItemSelected],
  );

  async function handleDownloadXml(
    recipientInvoice: ManifestationRecipientInvoice,
  ) {
    if (recipientInvoice.filePath) {
      const filePathSplit = recipientInvoice.filePath.split(';');
      const subdirectory = filePathSplit[0];
      const key = filePathSplit[1];

      const result = await downloadXmlMutate({
        key,
        subdirectory,
      });

      report.downloadFile({
        result,
        fileName: `${recipientInvoice.nfeKey}.xml`,
      });
    }
  }

  const columns: ColumnsType<ManifestationRecipientInvoice> = [
    {
      dataIndex: 'situation',
      title: 'Situação',
      ellipsis: true,
      width: 180,
      render: (value: RecipientInvoiceSituation) => {
        if (value) {
          if (value === RecipientInvoiceSituation.noSituation) {
            return (
              <Tooltip
                title="XML foi disponibilizado apos pesquisa da nota na 
              SEFAZ mas sem a informação da situação se a nota ja foi manifestada ou não"
              >
                <StatusTag label={RecipientInvoiceSituationLabel[value]} />
              </Tooltip>
            );
          }
          return <StatusTag label={RecipientInvoiceSituationLabel[value]} />;
        }
        return '-';
      },
    },
    {
      dataIndex: 'eventDateTime',
      title: 'Data',
      ellipsis: true,
      width: 140,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      dataIndex: 'nsu',
      title: 'NSU',
      ellipsis: true,
      width: 150,
    },
    {
      dataIndex: 'nfeKey',
      title: 'NFE',
      render: (value) => value || '-',
      ellipsis: true,
      width: 400,
    },
    {
      dataIndex: 'issuer',
      title: 'Fornecedor',
      render: (value) => value || '-',
      ellipsis: true,
      width: 300,
    },
    {
      dataIndex: 'value',
      title: 'Valor',
      render: (value) => formatterPrice(value ?? 0),
      ellipsis: true,
      width: 70,
      fixed: 'right',
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="customers.list.header.actions" />,
      render: (_, row) => (
        <Space direction="horizontal">
          <DownloadIcon
            iconProps={{ onClick: () => handleDownloadXml(row) }}
            content="Baixar XML"
            disabled={!row.filePath}
          />

          <EditIcon
            disabled={
              ![
                RecipientInvoiceSituation.pending,
                RecipientInvoiceSituation.confirmed,
              ].includes(row.situation)
            }
            iconProps={{ onClick: () => handleEditInvoice(row) }}
          />
        </Space>
      ),
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <InfiniteTable<ManifestationRecipientInvoice>
        loading={isLoading}
        columns={columns}
        dataSource={recipientInvoiceList}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        rowKey={(row) => row.id}
        scroll={{ x: getTableScrollX(columns) }}
        pagination={false}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: manifestationItems.map((item) => item.id),
          onChange: (_, rows) =>
            setManifestationItems((prev) => {
              const itemsToAdd = rows.filter(
                (row) => !prev.some((item) => item.id === row.id),
              );

              const newPrev = prev.filter((item) =>
                rows.some((row) => row.id === item.id),
              );

              if (itemsToAdd.length === 1) {
                handleEditInvoice(itemsToAdd[0]);
              }

              return [...newPrev, ...itemsToAdd];
            }),
          getCheckboxProps: (row) => ({
            disabled: ![
              RecipientInvoiceSituation.pending,
              RecipientInvoiceSituation.confirmed,
              RecipientInvoiceSituation.noSituation,
            ].includes(row.situation),
          }),
        }}
      />

      <RecipientInvoiceEditModal
        open={modalEditOpened}
        onCancel={modalEditAction.close}
        onCallback={handleManifestationItems}
        manifestationRecipientSelect={manifestationItems.find(
          (item) => item.id === manifestationItemSelected?.id,
        )}
      />
    </>
  );
};

export default ManifestationRecipientList;
